/**
 * Quiz Warning Component
 * Shows warning modal before starting a timed quiz
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { FiAlertTriangle, FiClock, FiArrowLeft } from 'react-icons/fi';
import quizApiService from '../../services/quizApi';

const Container = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--background-overlay);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  z-index: 1000;
`;

const WarningCard = styled.div`
  background: var(--background-primary);
  border-radius: 0;
  padding: 2rem;
  width: 100%;
  max-width: 500px;
  box-shadow: var(--shadow-xl);
  
  @media (max-width: 768px) {
    padding: 1.5rem;
    border-radius: 0;
    max-width: 90%;
  }
  
  @media (max-width: 480px) {
    padding: 1rem;
    margin: 0.5rem;
    max-width: 95%;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
  
  @media (max-width: 480px) {
    gap: 0.75rem;
    margin-bottom: 1rem;
  }
`;

const Icon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  background: rgba(246, 224, 94, 0.1);
  border-radius: 0;
  color: var(--warning-dark);
  flex-shrink: 0;
  
  @media (max-width: 480px) {
    width: 40px;
    height: 40px;
    border-radius: 0;
  }
`;

const Title = styled.h1`
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
  
  @media (max-width: 480px) {
    font-size: 1.25rem;
  }
`;

const Message = styled.div`
  font-size: 1rem;
  color: var(--text-primary);
  line-height: 1.5;
  margin: 0 0 2rem 0;
  background: var(--background-tertiary);
  padding: 1.5rem;
  border-radius: 0;
  border-left: 4px solid var(--warning-color);
  position: relative;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  
  strong {
    margin: 0;
    padding: 0;
    color: var(--text-primary);
  }
`;

const TimeHighlight = styled.span`
  background: rgba(246, 224, 94, 0.1);
  color: var(--warning-dark);
  padding: 0.25rem 0.5rem;
  border-radius: 0;
  font-weight: 700;
  border: 1px solid var(--warning-color);
  display: inline-block;
`;

const Actions = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  
  @media (max-width: 480px) {
    flex-direction: column;
    gap: 0.75rem;
  }
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  border-radius: 0;
  font-weight: 600;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 2px solid transparent;
  min-width: 100px;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &.primary {
    background: var(--primary-color);
    color: var(--text-inverse);
    border-color: var(--primary-color);

    &:hover {
      background: var(--primary-dark);
      border-color: var(--primary-dark);
    }
  }

  &.secondary {
    background: transparent;
    color: var(--text-primary);
    border-color: var(--border-color);

    &:hover {
      background: var(--background-tertiary);
      border-color: var(--border-dark);
    }
  }
  
  @media (max-width: 480px) {
    padding: 0.875rem 1.25rem;
    font-size: 0.9rem;
    width: 100%;
    min-width: auto;
  }
`;

const LoadingState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  padding: 2rem;
  color: var(--text-secondary);
`;

const ErrorState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  padding: 2rem;
  width: 100%;
  max-width: 500px;
  background: var(--background-primary);
  color: var(--text-primary);
  border-radius: 0;
  box-shadow: var(--shadow-lg);
  border-left: 4px solid var(--error-color);

  svg {
    color: var(--error-color);
  }

  p {
    margin: 0;
    text-align: center;
  }
`;

function QuizWarning() {
  const { assignmentId } = useParams();
  const navigate = useNavigate();
  const [assignment, setAssignment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isStarting, setIsStarting] = useState(false);

  useEffect(() => {
    const fetchAssignment = async () => {
      try {
        setLoading(true);
        const response = await quizApiService.getStudentAssignmentDetails(assignmentId);
        
        if (response.success) {
          setAssignment(response.data);
        } else {
          setError(response.message || 'Failed to load assignment');
        }
      } catch (err) {
        console.error('Error fetching assignment:', err);
        console.error('Assignment ID:', assignmentId);
        setError('Failed to load assignment. Please check if the assignment exists and you have access to it.');
      } finally {
        setLoading(false);
      }
    };

    fetchAssignment();
  }, [assignmentId]);

  const handleProceed = () => {
    // Prevent multiple clicks while starting
    if (isStarting) return;
    
    setIsStarting(true);
    navigate(`/student/assignments/${assignmentId}/start`);
  };

  const handleCancel = () => {
    navigate('/student/quizzes');
  };

  if (loading) {
    return (
      <Container>
        <LoadingState>
          <FiClock size={24} />
          <p>Loading quiz information...</p>
        </LoadingState>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <ErrorState>
          <FiAlertTriangle size={24} />
          <p>{error}</p>
          <Button className="secondary" onClick={handleCancel}>
            <FiArrowLeft size={16} />
            Back to Quizzes
          </Button>
        </ErrorState>
      </Container>
    );
  }

  if (!assignment) {
    return (
      <Container>
        <ErrorState>
          <FiAlertTriangle size={24} />
          <p>Assignment not found</p>
          <Button className="secondary" onClick={handleCancel}>
            <FiArrowLeft size={16} />
            Back to Quizzes
          </Button>
        </ErrorState>
      </Container>
    );
  }

  const isTimed = assignment.time_limit_minutes && assignment.time_limit_minutes > 0;
  const isSingleAttempt = assignment.attempts_allowed === 1;
  
  // Check if quiz is past due - consider submission time if viewing existing submission
  const dueDate = assignment.due_at;
  const isLate = dueDate && (() => {
    if (assignment.submitted_at) {
      // For existing submissions, check if submission was made after due date
      try {
        const submittedAt = new Date(assignment.submitted_at);
        const dueDateObj = new Date(dueDate);
        return submittedAt > dueDateObj;
      } catch (error) {
        console.error('Error checking if quiz submission was late:', error);
        return false;
      }
    } else {
      // For new submissions, check if current time is past due date
      return new Date() > new Date(dueDate);
    }
  })();
  
  // Determine the title based on quiz type
  const getTitle = () => {
    if (isLate) {
      return 'Late Submission Warning';
    } else if (isSingleAttempt) {
      return 'Single Attempt';
    } else if (isTimed) {
      return 'Timed Assessment';
    } else {
      return 'Quiz Warning';
    }
  };

  return (
    <Container>
      <WarningCard>
        <Header>
          <Icon>
            <FiAlertTriangle size={24} />
          </Icon>
          <Title>{getTitle()}</Title>
        </Header>
        
        {isLate ? (
          <Message>
            <strong>⚠️ Late Submission:</strong> {assignment.submitted_at ? 
              'This quiz was submitted past due date/time. Your submission will be marked as late.' :
              'This quiz is past its due date. Your submission will be marked as late.'
            }
            {isTimed && (
              <> You will have <TimeHighlight>{assignment.time_limit_minutes} minutes</TimeHighlight> to complete this quiz.</>
            )}
            {isSingleAttempt && (
              <> This quiz allows only one attempt, so make sure you're ready to complete it.</>
            )}
            {!isTimed && !isSingleAttempt && (
              <> This quiz has no time limit and allows multiple attempts.</>
            )}
            <br /><br />
            <strong>Important:</strong> Late submissions may be subject to grade penalties as determined by your instructor.
          </Message>
        ) : isTimed ? (
          <Message>
            You will have <TimeHighlight>{assignment.time_limit_minutes} minutes</TimeHighlight> to complete this quiz. It will be automatically submitted when the time ends, unless you choose to submit it earlier. Please be aware that closing your browser or leaving this page will also submit your quiz. To avoid interruptions, we kindly recommend ensuring a stable internet connection and setting aside enough uninterrupted time before you begin.
          </Message>
        ) : assignment.attempts_allowed === 1 ? (
          <Message>
            <strong>Single Attempt Quiz:</strong> This quiz allows only one attempt. Once you start, you must complete it in this session. 
            If you leave the page or close your browser, your quiz will be automatically submitted and graded with your current answers. 
            Make sure you have enough time to complete the entire quiz.
          </Message>
        ) : (
          <Message>
            <strong>Ready to start:</strong> This quiz has no time limit and allows multiple attempts. You can take your time to complete it, 
            and your progress will be saved automatically. You can close your browser and return later to continue where you left off.
          </Message>
        )}
        
        <Actions>
          <Button className="secondary" onClick={handleCancel}>
            <FiArrowLeft size={16} />
            Cancel
          </Button>
          <Button 
            className="primary" 
            onClick={handleProceed}
            disabled={isStarting}
            style={{ 
              opacity: isStarting ? 0.7 : 1,
              cursor: isStarting ? 'not-allowed' : 'pointer'
            }}
          >
            <FiClock size={16} />
            {isStarting ? 'Starting...' : 'Proceed'}
          </Button>
        </Actions>
      </WarningCard>
    </Container>
  );
}

export default QuizWarning;
